<template>
	<v-container>
		<!-- RESULT VIEW -->
		<div v-if="submitted && resultData">
			<div class="download-wrapper">
				<div class="download-dropdown-wrapper" v-click-outside="closeDropdown">
					<button class="download-btn" @click="toggleDropdown">
						<v-icon x-small left>fas fa-download</v-icon>
						Download VSME Document
						<v-icon x-small right style="margin-left:6px;">
							{{ showDownloadDropdown ? 'fas fa-chevron-up' : 'fas fa-chevron-down' }}
						</v-icon>
					</button>
					<div v-if="showDownloadDropdown" class="download-dropdown-menu">
						<button class="dropdown-item" @click="handleDownloadPDF">
							<v-icon small left class="icon-pdf">fas fa-file-pdf</v-icon>
							As a PDF
						</button>
						<button class="dropdown-item" @click="handleDownloadJSON">
							<v-icon small left class="icon-json">fas fa-file-code</v-icon>
							As a JSON
						</button>
					</div>
				</div>
			</div>
			<v-card class="pa-6 mb-6" outlined>

				<div class="header-bar">
					<div>
						<h2>Diversity & Equality</h2>
					</div>

					<div style="display:flex; gap:8px; align-items:center;">
						<button class="btn-outline" @click="enableEdit">
							<v-icon x-small left>fas fa-pencil</v-icon>
							Edit
						</button>

						<span v-if="isDraft" class="draft-badge">
							<v-icon x-small style="margin-right:4px;">fas fa-clock</v-icon>
							Draft
						</span>

						<span v-else class="submitted-badge">
							<svg width="12" height="12" viewBox="0 0 12 12" fill="none" style="flex-shrink:0">
								<circle cx="6" cy="6" r="5.5" stroke="currentColor" stroke-width="1"/>
								<path d="M3.5 6L5.2 7.7L8.5 4.3" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
							</svg>
							Submitted
						</span>
					</div>
				</div>

				<!-- Workforce -->
				<div class="section">
					<div class="section-title">1 · Workforce overview / 2 · Gender Distribution overview</div>

					<div class="metric-grid">
						<div class="metric-card">
							<p class="metric-label">Total employees</p>
							<p class="metric-value">{{ fmt(resultData.totalEmployees) }}</p>
						</div>

						<div class="metric-card">
							<p class="metric-label">Male</p>
							<p class="metric-value">{{ fmt(resultData.genderMale) }}</p>
						</div>

						<div class="metric-card">
							<p class="metric-label">Female</p>
							<p class="metric-value">{{ fmt(resultData.genderFemale) }}</p>
						</div>

						<div class="metric-card">
							<p class="metric-label">Other / NB</p>
							<p class="metric-value">{{ fmt(resultData.genderOther) }}</p>
						</div>
					</div>

					<!-- ADD THIS BLOCK HERE -->
					<div style="margin-top:10px;">
						<div class="bar-wrap">
							<div class="bar-label">
								<span>Male</span>
								<span>{{ genderMalePct }}%</span>
							</div>

							<div class="bar-track">
								<div
									class="bar-fill"
									:style="{ background: '#185FA5', width: genderMalePct + '%' }"
								></div>
							</div>
						</div>

						<div class="bar-wrap">
							<div class="bar-label">
								<span>Female</span>
								<span>{{ genderFemalePct }}%</span>
							</div>

							<div class="bar-track">
								<div
									class="bar-fill"
									:style="{ background: '#0F6E56', width: genderFemalePct + '%' }"
								></div>
							</div>
						</div>
					</div>

					<hr class="divider">

					<div class="sub-label">By job level</div>

					<div style="margin-bottom: 0.75rem;">
						<div class="sub-label" style="font-size:11px; text-transform:uppercase; letter-spacing:0.05em; color:#555; margin-bottom:6px;">Leadership / Management</div>
						<div class="row-grid">
							<div class="row-item">
								<span class="row-key">Male</span>
								<span class="row-val">{{ fmt(resultData.leaderMale) }}</span>
							</div>
							<div class="row-item">
								<span class="row-key">Female</span>
								<span class="row-val">{{ fmt(resultData.leaderFemale) }}</span>
							</div>
							<div class="row-item">
								<span class="row-key">Other / NB</span>
								<span class="row-val">{{ fmt(resultData.leaderOther) }}</span>
							</div>
						</div>
					</div>

					<div>
						<div class="sub-label" style="font-size:11px; text-transform:uppercase; letter-spacing:0.05em; color:#555; margin-bottom:6px;">Staff / Other Employees</div>
						<div class="row-grid">
							<div class="row-item">
								<span class="row-key">Male</span>
								<span class="row-val">{{ fmt(resultData.staffMale) }}</span>
							</div>
							<div class="row-item">
								<span class="row-key">Female</span>
								<span class="row-val">{{ fmt(resultData.staffFemale) }}</span>
							</div>
							<div class="row-item">
								<span class="row-key">Other / NB</span>
								<span class="row-val">{{ fmt(resultData.staffOther) }}</span>
							</div>
						</div>
					</div>
				</div>

				<!-- Age -->
				<div class="section">
					<div class="section-title">3 · Age distribution</div>

					<div v-for="age in ageFields" :key="age.model" class="bar-wrap">
						<div class="bar-label">
							<span>{{ age.label }}</span>
							<span>{{ fmt(resultData[age.model]) }}</span>
						</div>

						<div class="bar-track">
							<div
								class="bar-fill"
								:style="{ background: '#3B6D11', width: agePct(resultData[age.model]) + '%' }"
							></div>
						</div>
					</div>
				</div>

				<!-- Nationality -->
				<div class="section">
					<div class="section-title">4 · Nationalities & demographics</div>

					<div class="row-grid">
						<div class="row-item">
							<span class="row-key">Nationalities</span>
							<span class="row-val">{{ fmt(resultData.nationalities) }}</span>
						</div>

						<div class="row-item">
							<span class="row-key">Additional demographics</span>
							<span class="row-val">{{ fmt(resultData.additionalDemographics) }}</span>
						</div>
					</div>
				</div>

				<!-- Equality & Pay -->
				<div class="section">
					<div class="section-title">5 · Equality & Pay Metrics</div>

					<div class="row-grid" style="margin-bottom:1rem;">
						<div class="row-item">
							<span class="row-key">Gender pay gap tracked?</span>
							<span class="row-val" v-html="badge(resultData.trackPayGap)"></span>
						</div>

						<div class="row-item">
							<span class="row-key">Pay gap</span>
							<span class="row-val">
								{{ resultData.trackPayGap === 'Yes' ? fmt(resultData.payGap, '%') : '—' }}
							</span>
						</div>

						<div class="row-item">
							<span class="row-key">Underrepresented in leadership</span>
							<span class="row-val">
								{{ fmt(resultData.underrepresentedLeadership, '%') }}
							</span>
						</div>
					</div>

					<hr class="divider">

					<div class="sub-label">Promotion rate by gender</div>

					<div class="metric-grid" style="margin-bottom:1rem;">
						<div class="metric-card">
							<p class="metric-label">Male</p>
							<p class="metric-value">{{ fmt(resultData.promoMale, '%') }}</p>
						</div>

						<div class="metric-card">
							<p class="metric-label">Female</p>
							<p class="metric-value">{{ fmt(resultData.promoFemale, '%') }}</p>
						</div>

						<div class="metric-card">
							<p class="metric-label">Other / NB</p>
							<p class="metric-value">{{ fmt(resultData.promoOther, '%') }}</p>
						</div>
					</div>

					<div class="sub-label">Turnover rate by gender</div>

					<div class="metric-grid">
						<div class="metric-card">
							<p class="metric-label">Male</p>
							<p class="metric-value">{{ fmt(resultData.turnoverMale, '%') }}</p>
						</div>

						<div class="metric-card">
							<p class="metric-label">Female</p>
							<p class="metric-value">{{ fmt(resultData.turnoverFemale, '%') }}</p>
						</div>

						<div class="metric-card">
							<p class="metric-label">Other / NB</p>
							<p class="metric-value">{{ fmt(resultData.turnoverOther, '%') }}</p>
						</div>
					</div>
				</div>

				<!-- Policies & Governance -->
				<div class="section">
					<div class="section-title">6 · Policies & Governance</div>

					<div class="row-grid">
						<div class="row-item">
							<span class="row-key">D&I policy in place?</span>
							<span class="row-val" v-html="badge(resultData.diPolicy)"></span>
						</div>

						<div class="row-item">
							<span class="row-key">Policy reference</span>
							<span class="row-val">{{ fmt(resultData.policyRef) }}</span>
						</div>

						<div class="row-item">
							<span class="row-key">Responsible role</span>
							<span class="row-val">{{ fmt(resultData.responsibleRole) }}</span>
						</div>

						<div class="row-item">
							<span class="row-key">Diversity targets set?</span>
							<span class="row-val" v-html="badge(resultData.hasTargets)"></span>
						</div>
					</div>

					<div
						v-if="resultData.hasTargets === 'Yes' && resultData.targetsDescription"
						class="text-block"
						style="margin-top:1rem;"
					>
						<div class="row-key" style="margin-bottom:4px;">
							Target description
						</div>

						<div class="text-body">
							{{ resultData.targetsDescription }}
						</div>
					</div>
				</div>

				<!-- Inclusion Initiatives -->
				<div class="section">
					<div class="section-title">7 · Inclusion initiatives</div>

					<div class="tag-list" style="margin-bottom:1rem;">
						<template v-if="allInitiatives.length">
							<span
								v-for="item in allInitiatives"
								:key="item"
								class="tag"
							>
								{{ item }}
							</span>
						</template>

						<span v-else class="empty-note">
							None reported
						</span>
					</div>

					<hr class="divider">

					<div class="row-grid">
						<div class="row-item">
							<span class="row-key">Provides D&I training?</span>
							<span class="row-val" v-html="badge(resultData.hasTraining)"></span>
						</div>

						<div class="row-item">
							<span class="row-key">Training frequency</span>

							<span class="row-val">
								{{
									resultData.hasTraining === 'Yes' &&
									resultData.trainingFrequency
										? resultData.trainingFrequency + 'x per year'
										: '—'
								}}
							</span>
						</div>
					</div>

					<div
						v-if="resultData.hasTraining === 'Yes' && resultData.trainingDescription"
						class="text-block"
						style="margin-top:1rem;"
					>
						<div class="row-key" style="margin-bottom:4px;">
							Training description
						</div>

						<div class="text-body">
							{{ resultData.trainingDescription }}
						</div>
					</div>
				</div>

				<!-- Progress & Improvement -->
				<div class="section">
					<div class="section-title">8 · Progress & Improvement</div>

					<div v-if="resultData.diversityChange" style="margin-bottom:1rem;">
						<div class="row-key" style="margin-bottom:4px;">
							How diversity profile has changed
						</div>

						<div class="text-body">
							{{ resultData.diversityChange }}
						</div>
					</div>

					<div v-if="resultData.improvements" style="margin-top:1rem;">
						<div class="row-key" style="margin-bottom:4px;">
							Measurable improvements from initiatives
						</div>

						<div class="text-body">
							{{ resultData.improvements }}
						</div>
					</div>

					<div class="row-item" style="margin-top:1rem;">
						<span class="row-key" style="margin-bottom:4px;">
							Tracks diversity metrics?
						</span>

						<span class="row-val" v-html="badge(resultData.trackMetrics)"></span>
					</div>

					<div
						v-if="resultData.metricsTracked && resultData.metricsTracked.length"
						class="tag-list"
						style="margin-top:0.5rem;"
					>
						<span
							v-for="m in resultData.metricsTracked"
							:key="m"
							class="tag"
						>
							{{ m }}
						</span>
					</div>
				</div>
			</v-card>
		</div>
		<v-form v-else ref="form" v-model="formIsValid">
			<v-card class="pa-3 mb-6" outlined>
				<v-alert
					v-if="isDraft"
					type="warning"
					dense
					outlined
					class="mb-3"
					icon="fas fa-clock"
				>
					You have a saved draft. Complete and submit when ready.
				</v-alert>
				<v-col cols="12" class="d-flex justify-center"><h2>DIVERSITY & EQUALITY</h2></v-col>

				<!-- 1. Workforce Overview -->
				<v-row class="contentRow">
					<v-col cols="12"><h2>1. Workforce Overview</h2></v-col>
					<v-col cols="12">
						<v-text-field v-model="form.totalEmployees" label="Q1. Total number of employees" type="number" filled dense />
					</v-col>
					<v-col cols="12" class="desc-wrapper">
						<v-btn text small color="primary" @click="toggleDesc('q1')">
							{{ showDesc.q1 ? 'Hide description' : 'Show description' }}
							<v-icon x-small class="icon-circle ml-2">fas fa-section</v-icon>
						</v-btn>
						<div v-if="showDesc.q1" class="grey--text text--darken-1 mt-1 text-caption">
							(ESRS S1-6, paragraph 50: The undertaking shall disclose the total number of employees and workers in its own workforce, including breakdowns where relevant to understand workforce composition.)
						</div>
					</v-col>
				</v-row>

				<!-- 2. Gender Distribution -->
				<v-row class="contentRow">
					<v-col cols="12"><h2>2. Gender Distribution</h2></v-col>

					<v-col cols="12"><h3>Q2. Total gender distribution</h3></v-col>
					<v-col cols="12" md="4"><v-text-field v-model="form.genderMale"   label="Male"                       type="number" filled dense /></v-col>
					<v-col cols="12" md="4"><v-text-field v-model="form.genderFemale" label="Female"                     type="number" filled dense /></v-col>
					<v-col cols="12" md="4"><v-text-field v-model="form.genderOther"  label="Other / Non-binary (optional)" type="number" filled dense /></v-col>
					<v-col cols="12" class="desc-wrapper">
						<v-btn text small color="primary" @click="toggleDesc('q2')">{{ showDesc.q2 ? 'Hide description' : 'Show description' }}
							<v-icon x-small class="icon-circle ml-2">fas fa-section</v-icon>
						</v-btn>
						<div v-if="showDesc.q2" class="grey--text text--darken-1 mt-1 text-caption">
							(ESRS S1-6, paragraph 54(a): The undertaking shall disclose the distribution of employees by gender to provide insight into diversity within the workforce.)
						</div>
					</v-col>

					<v-col cols="12"><h3>Q3. Gender distribution across job levels</h3></v-col>
					<v-col cols="12"><p class="subtitle-2 grey--text">Leadership / Management</p></v-col>
					<v-col cols="12" md="4"><v-text-field v-model="form.leaderMale"   label="Male"                       type="number" filled dense /></v-col>
					<v-col cols="12" md="4"><v-text-field v-model="form.leaderFemale" label="Female"                     type="number" filled dense /></v-col>
					<v-col cols="12" md="4"><v-text-field v-model="form.leaderOther"  label="Other / Non-binary (optional)" type="number" filled dense /></v-col>

					<v-col cols="12"><p class="subtitle-2 grey--text">Staff / Other Employees</p></v-col>
					<v-col cols="12" md="4"><v-text-field v-model="form.staffMale"    label="Male"                       type="number" filled dense /></v-col>
					<v-col cols="12" md="4"><v-text-field v-model="form.staffFemale"  label="Female"                     type="number" filled dense /></v-col>
					<v-col cols="12" md="4"><v-text-field v-model="form.staffOther"   label="Other / Non-binary (optional)" type="number" filled dense /></v-col>
					<v-col cols="12" class="desc-wrapper">
						<v-btn text small color="primary" @click="toggleDesc('q3')">{{ showDesc.q3 ? 'Hide description' : 'Show description' }}
							<v-icon x-small class="icon-circle ml-2">fas fa-section</v-icon>
						</v-btn>
						<div v-if="showDesc.q3" class="grey--text text--darken-1 mt-1 text-caption">
							(ESRS S1-9, paragraph 68(a): The undertaking shall disclose the representation of women and men at different levels of the organization.)
						</div>
					</v-col>
				</v-row>

				<!-- 3. Age Distribution -->
				<v-row class="contentRow">
					<v-col cols="12"><h2>3. Age Distribution</h2></v-col>
					<v-col cols="12"><h3>Q4. Age distribution of employees</h3></v-col>
					<v-col cols="12" md="4" v-for="age in ageFields" :key="age.model">
						<v-text-field v-model="form[age.model]" :label="age.label" type="number" filled dense />
					</v-col>
					<v-col cols="12" class="desc-wrapper">
						<v-btn text small color="primary" @click="toggleDesc('q4')">{{ showDesc.q4 ? 'Hide description' : 'Show description' }}
							<v-icon x-small class="icon-circle ml-2">fas fa-section</v-icon>
						</v-btn>
						<div v-if="showDesc.q4" class="grey--text text--darken-1 mt-1 text-caption">
							(ESRS S1-6, paragraph 54(b): The undertaking shall provide a breakdown of employees by age groups.)
						</div>
					</v-col>
				</v-row>

				<!-- 4. Nationality & Optional Demographics -->
				<v-row class="contentRow">
					<v-col cols="12"><h2>4. Nationality & Optional Demographics</h2></v-col>
					<v-col cols="12">
						<v-textarea v-model="form.nationalities" label="Q5. Nationalities represented (optional)" filled dense rows="3" />
					</v-col>
					<v-col cols="12" class="desc-wrapper">
						<v-btn text small color="primary" @click="toggleDesc('q5')">{{ showDesc.q5 ? 'Hide description' : 'Show description' }}
							<v-icon x-small class="icon-circle ml-2">fas fa-section</v-icon>
						</v-btn>
						<div v-if="showDesc.q5" class="grey--text text--darken-1 mt-1 text-caption">
							(ESRS S1-7, paragraph 57: Disclose diversity characteristics including nationality or geographic background.)
						</div>
					</v-col>
					<v-col cols="12">
						<v-textarea v-model="form.additionalDemographics" label="Q6. Additional demographic information (optional – e.g. disability status, veteran status)" filled dense rows="3" />
					</v-col>
					<v-col cols="12" class="desc-wrapper">
						<v-btn text small color="primary" @click="toggleDesc('q6')">{{ showDesc.q6 ? 'Hide description' : 'Show description' }}
							<v-icon x-small class="icon-circle ml-2">fas fa-section</v-icon>
						</v-btn>
						<div v-if="showDesc.q6" class="grey--text text--darken-1 mt-1 text-caption">
							(ESRS S1-7, paragraph 58: Additional diversity characteristics such as disability status or underrepresented groups.)
						</div>
					</v-col>
				</v-row>

				<!-- 5. Equality & Pay Metrics -->
				<v-row class="contentRow">
					<v-col cols="12"><h2>5. Equality & Pay Metrics</h2></v-col>

					<v-col cols="12">
						<v-select v-model="form.trackPayGap" :items="['Yes', 'No']" label="Q7. Do you track gender pay gap?" filled dense />
					</v-col>
					<v-col cols="12" md="6" v-if="form.trackPayGap === 'Yes'">
						<v-text-field v-model="form.payGap" label="Gender pay gap (%)" type="number" filled dense />
					</v-col>
					<v-col cols="12" class="desc-wrapper">
						<v-btn text small color="primary" @click="toggleDesc('q7')">{{ showDesc.q7 ? 'Hide description' : 'Show description' }}
							<v-icon x-small class="icon-circle ml-2">fas fa-section</v-icon>
						</v-btn>
						<div v-if="showDesc.q7" class="grey--text text--darken-1 mt-1 text-caption">
							(ESRS S1-16, paragraph 87: Disclose the gender pay gap and explain differences in average pay.)
						</div>
					</v-col>

					<v-col cols="12"><h3>Q8. Promotion rate by gender (%)</h3></v-col>
					<v-col cols="12" md="4"><v-text-field v-model="form.promoMale"   label="Male (%)"             type="number" filled dense /></v-col>
					<v-col cols="12" md="4"><v-text-field v-model="form.promoFemale" label="Female (%)"           type="number" filled dense /></v-col>
					<v-col cols="12" md="4"><v-text-field v-model="form.promoOther"  label="Other / Non-binary (%)" type="number" filled dense /></v-col>
					<v-col cols="12" class="desc-wrapper">
						<v-btn text small color="primary" @click="toggleDesc('q8')">{{ showDesc.q8 ? 'Hide description' : 'Show description' }}
							<v-icon x-small class="icon-circle ml-2">fas fa-section</v-icon>
						</v-btn>
						<div v-if="showDesc.q8" class="grey--text text--darken-1 mt-1 text-caption">
							(ESRS S1-6, paragraph 55(c): Career progression indicators such as promotion rates.)
						</div>
					</v-col>

					<v-col cols="12"><h3>Q9. Employee turnover rate by gender (%)</h3></v-col>
					<v-col cols="12" md="4"><v-text-field v-model="form.turnoverMale"   label="Male (%)"             type="number" filled dense /></v-col>
					<v-col cols="12" md="4"><v-text-field v-model="form.turnoverFemale" label="Female (%)"           type="number" filled dense /></v-col>
					<v-col cols="12" md="4"><v-text-field v-model="form.turnoverOther"  label="Other / Non-binary (%)" type="number" filled dense /></v-col>
					<v-col cols="12" class="desc-wrapper">
						<v-btn text small color="primary" @click="toggleDesc('q9')">{{ showDesc.q9 ? 'Hide description' : 'Show description' }}
							<v-icon x-small class="icon-circle ml-2">fas fa-section</v-icon>
						</v-btn>
						<div v-if="showDesc.q9" class="grey--text text--darken-1 mt-1 text-caption">
							(ESRS S1-6, paragraph 55(b): Employee turnover rates to provide insight into workforce stability.)
						</div>
					</v-col>

					<v-col cols="12">
						<v-text-field v-model="form.underrepresentedLeadership" label="Q10. % of leadership roles held by underrepresented groups" type="number" filled dense />
					</v-col>
					<v-col cols="12" class="desc-wrapper">
						<v-btn text small color="primary" @click="toggleDesc('q10')">{{ showDesc.q10 ? 'Hide description' : 'Show description' }}
							<v-icon x-small class="icon-circle ml-2">fas fa-section</v-icon>
						</v-btn>
						<div v-if="showDesc.q10" class="grey--text text--darken-1 mt-1 text-caption">
							(ESRS S1-9, paragraph 68(b): Diversity of governance and leadership bodies.)
						</div>
					</v-col>
				</v-row>

				<!-- 6. Policies & Governance -->
				<v-row class="contentRow">
					<v-col cols="12"><h2>6. Policies & Governance</h2></v-col>

					<v-col cols="12">
						<v-select v-model="form.diPolicy" :items="['Yes', 'No']" label="Q11. Do you have a formal D&I policy?" filled dense />
					</v-col>
					<v-col cols="12" md="6" v-if="form.diPolicy === 'Yes'">
						<v-text-field v-model="form.policyRef" label="Policy reference or link" filled dense />
					</v-col>
					<v-col cols="12" class="desc-wrapper">
						<v-btn text small color="primary" @click="toggleDesc('q11')">{{ showDesc.q11 ? 'Hide description' : 'Show description' }}
							<v-icon x-small class="icon-circle ml-2">fas fa-section</v-icon>
						</v-btn>
						<div v-if="showDesc.q11" class="grey--text text--darken-1 mt-1 text-caption">
							(ESRS S1-1, paragraph 16: Policies related to equal treatment and opportunities.)
						</div>
					</v-col>

					<v-col cols="12">
						<v-text-field v-model="form.responsibleRole" label="Q12. Who is responsible for D&I? (Role / Title)" filled dense />
					</v-col>
					<v-col cols="12" class="desc-wrapper">
						<v-btn text small color="primary" @click="toggleDesc('q12')">{{ showDesc.q12 ? 'Hide description' : 'Show description' }}
							<v-icon x-small class="icon-circle ml-2">fas fa-section</v-icon>
						</v-btn>
						<div v-if="showDesc.q12" class="grey--text text--darken-1 mt-1 text-caption">
							(ESRS 2 GOV-1, paragraph 21: Governance structures and roles responsible for sustainability matters.)
						</div>
					</v-col>

					<v-col cols="12">
						<v-select v-model="form.hasTargets" :items="['Yes', 'No']" label="Q13. Are diversity or equality targets defined?" filled dense />
					</v-col>
					<v-col cols="12" v-if="form.hasTargets === 'Yes'">
						<v-textarea v-model="form.targetsDescription" label="Description of targets" filled dense rows="3" />
					</v-col>
					<v-col cols="12" class="desc-wrapper">
						<v-btn text small color="primary" @click="toggleDesc('q13')">{{ showDesc.q13 ? 'Hide description' : 'Show description' }}
							<v-icon x-small class="icon-circle ml-2">fas fa-section</v-icon>
						</v-btn>
						<div v-if="showDesc.q13" class="grey--text text--darken-1 mt-1 text-caption">
							(ESRS S1-5, paragraph 43: Time-bound targets related to workforce equality and diversity.)
						</div>
					</v-col>
				</v-row>

				<!-- 7. Inclusion Initiatives -->
				<v-row class="contentRow">
					<v-col cols="12"><h2>7. Inclusion Initiatives</h2></v-col>
					<v-col cols="12">
						<h3>Q14. Which inclusion initiatives are in place? (Select all that apply)</h3>
						<v-checkbox v-model="form.initiatives" label="Mentorship programs"       value="Mentorship programs"       hide-details />
						<v-checkbox v-model="form.initiatives" label="Diversity council"          value="Diversity council"          hide-details />
						<v-checkbox v-model="form.initiatives" label="Employee resource groups"   value="Employee resource groups"   hide-details />
						<v-checkbox v-model="form.initiatives" label="Inclusive hiring practices" value="Inclusive hiring practices" hide-details />
						<v-checkbox v-model="form.initiatives" label="Pay equity audits"          value="Pay equity audits"          hide-details />
						<v-checkbox v-model="form.initiativesHasOther" label="Other" hide-details />
						<v-text-field v-if="form.initiativesHasOther" v-model="form.initiativesOther" label="Please specify" filled dense class="mt-2" />
					</v-col>
					<v-col cols="12" class="desc-wrapper mt-2">
						<v-btn text small color="primary" @click="toggleDesc('q14')">{{ showDesc.q14 ? 'Hide description' : 'Show description' }}
							<v-icon x-small class="icon-circle ml-2">fas fa-section</v-icon>
						</v-btn>
						<div v-if="showDesc.q14" class="grey--text text--darken-1 mt-1 text-caption">
							(ESRS S1-1, paragraph 17: Actions and initiatives implemented to promote equal opportunities and inclusion.)
						</div>
					</v-col>

					<v-col cols="12">
						<v-select v-model="form.hasTraining" :items="['Yes', 'No']" label="Q15. Do you provide D&I training?" filled dense />
					</v-col>
					<v-col cols="12" md="6" v-if="form.hasTraining === 'Yes'">
						<v-text-field v-model="form.trainingFrequency" label="How often is training conducted per year?" type="number" filled dense />
					</v-col>
					<v-col cols="12" v-if="form.hasTraining === 'Yes'">
						<v-textarea v-model="form.trainingDescription" label="Description of training" filled dense rows="3" />
					</v-col>
					<v-col cols="12" class="desc-wrapper">
						<v-btn text small color="primary" @click="toggleDesc('q15')">{{ showDesc.q15 ? 'Hide description' : 'Show description' }}
							<v-icon x-small class="icon-circle ml-2">fas fa-section</v-icon>
						</v-btn>
						<div v-if="showDesc.q15" class="grey--text text--darken-1 mt-1 text-caption">
							(ESRS S1-13, paragraph 79: Training and skills development initiatives including those related to D&I.)
						</div>
					</v-col>
				</v-row>

				<!-- 8. Progress & Improvement -->
				<v-row class="contentRow">
					<v-col cols="12"><h2>8. Progress & Improvement</h2></v-col>

					<v-col cols="12">
						<v-textarea v-model="form.diversityChange" label="Q16. How has your diversity profile changed over time?" filled dense rows="4" />
					</v-col>
					<v-col cols="12" class="desc-wrapper">
						<v-btn text small color="primary" @click="toggleDesc('q16')">{{ showDesc.q16 ? 'Hide description' : 'Show description' }}
							<v-icon x-small class="icon-circle ml-2">fas fa-section</v-icon>
						</v-btn>
						<div v-if="showDesc.q16" class="grey--text text--darken-1 mt-1 text-caption">
							(ESRS S1-6, paragraph 56: Comparative information over time to understand trends in workforce composition.)
						</div>
					</v-col>

					<v-col cols="12">
						<v-textarea v-model="form.improvements" label="Q17. Have inclusion initiatives led to measurable improvements?" filled dense rows="4" />
					</v-col>
					<v-col cols="12" class="desc-wrapper">
						<v-btn text small color="primary" @click="toggleDesc('q17')">{{ showDesc.q17 ? 'Hide description' : 'Show description' }}
							<v-icon x-small class="icon-circle ml-2">fas fa-section</v-icon>
						</v-btn>
						<div v-if="showDesc.q17" class="grey--text text--darken-1 mt-1 text-caption">
							(ESRS S1-4, paragraph 38: Effectiveness of actions taken to address workforce-related impacts.)
						</div>
					</v-col>

					<v-col cols="12">
						<v-select v-model="form.trackMetrics" :items="['Yes', 'No']" label="Q18. Do you track diversity metrics over time?" filled dense />
					</v-col>
					<v-col cols="12" v-if="form.trackMetrics === 'Yes'">
						<h3>If yes, which ones? (Select all that apply)</h3>
						<v-checkbox v-model="form.metricsTracked" label="Gender distribution" value="Gender distribution" hide-details />
						<v-checkbox v-model="form.metricsTracked" label="Pay equity"          value="Pay equity"          hide-details />
						<v-checkbox v-model="form.metricsTracked" label="Promotion rates"     value="Promotion rates"     hide-details />
						<v-checkbox v-model="form.metricsTracked" label="Leadership diversity" value="Leadership diversity" hide-details />
						<v-checkbox v-model="form.metricsTracked" label="Turnover rates"      value="Turnover rates"      hide-details />
					</v-col>
					<v-col cols="12" class="desc-wrapper mt-3">
						<v-btn text small color="primary" @click="toggleDesc('q18')">{{ showDesc.q18 ? 'Hide description' : 'Show description' }}
							<v-icon x-small class="icon-circle ml-2">fas fa-section</v-icon>
						</v-btn>
						<div v-if="showDesc.q18" class="grey--text text--darken-1 mt-1 text-caption">
							(ESRS 2 SBM-3, paragraph 48: Metrics used to monitor sustainability performance and progress over time.)
						</div>
					</v-col>
				</v-row>

				<!-- Error -->
				<v-row class="contentRow" v-if="errorMessage">
					<v-col cols="12">
						<v-alert type="error" dense>{{ errorMessage }}</v-alert>
					</v-col>
				</v-row>

				<!-- Actions -->
				<v-row class="contentRow">
					<v-col cols="12" class="d-flex justify-center">
						<v-btn outlined color="primary" class="mr-5" :loading="loading" :disabled="loading" @click="saveDraft">
							<v-icon x-small left>fas fa-floppy-disk</v-icon>
							Save as Draft
						</v-btn>
						<v-btn color="primary" :loading="loading" :disabled="loading" @click="submit">
							Submit
						</v-btn>
					</v-col>
				</v-row>
			</v-card>
		</v-form>
	</v-container>
</template>

<script>
import CsrAPI from "../api/csr/csr"

export default {
	props: {
		initialData: { type: Object, default: null },
		isEditMode:  { type: Boolean, default: false },
	},

	data() {
		return {
			formIsValid: false,
			loading: false,
			errorMessage: "",
			showDesc: {},
			ageFields: [
				{ label: "Under 25", model: "under25"  },
				{ label: "25–34",    model: "age25_34" },
				{ label: "35–44",    model: "age35_44" },
				{ label: "45–54",    model: "age45_54" },
				{ label: "55+",      model: "age55plus" },
			],
			form: {
				totalEmployees: null,
				genderMale: null, genderFemale: null, genderOther: null,
				leaderMale: null, leaderFemale: null, leaderOther: null,
				staffMale: null,  staffFemale: null,  staffOther: null,
				under25: null, age25_34: null, age35_44: null, age45_54: null, age55plus: null,
				nationalities: "", additionalDemographics: "",
				trackPayGap: null, payGap: null,
				promoMale: null, promoFemale: null, promoOther: null,
				turnoverMale: null, turnoverFemale: null, turnoverOther: null,
				underrepresentedLeadership: null,
				diPolicy: null, policyRef: "", responsibleRole: "",
				hasTargets: null, targetsDescription: "",
				initiatives: [], initiativesHasOther: false, initiativesOther: "",
				hasTraining: null, trainingFrequency: null, trainingDescription: "",
				diversityChange: "", improvements: "", trackMetrics: null, metricsTracked: [], isDraft: false,
			},
			submitted: false,
			resultData: null,
			isDraft: false,
			hasExistingRecord: false,
			showDownloadDropdown: false,
		}
	},

	watch: {
		initialData: {
			immediate: true,
			handler(res) {
				if (!res) return
				const safeParse = (val) => { try { return JSON.parse(val || "{}") } catch { return {} } }
				const gender      = safeParse(res.Gender_Distribution)
				const leader      = safeParse(res.Leadership_Gender)
				const staff       = safeParse(res.Staff_Gender)
				const age         = safeParse(res.Age_Distribution)
				const promo       = safeParse(res.Promotion_Rate)
				const turnover    = safeParse(res.Turnover_Rate)
				const initiatives = safeParse(res.Initiatives)
				const metrics     = safeParse(res.Metrics_Data)

				this.form = {
					...this.form,
					totalEmployees:             res.Total_Employees,
					genderMale: gender.male,    genderFemale: gender.female,    genderOther: gender.other,
					leaderMale: leader.male,    leaderFemale: leader.female,    leaderOther: leader.other,
					staffMale:  staff.male,     staffFemale:  staff.female,     staffOther:  staff.other,
					under25: age.under25,       age25_34: age.age25_34,         age35_44: age.age35_44,
					age45_54: age.age45_54,     age55plus: age.age55plus,
					nationalities:              res.Nationalities              || "",
					additionalDemographics:     res.Additional_Demographics    || "",
					trackPayGap:                res.Track_Pay_Gap,
					payGap:                     res.Pay_Gap,
					underrepresentedLeadership: res.Underrepresented_Leadership,
					promoMale: promo.male,      promoFemale: promo.female,     promoOther: promo.other,
					turnoverMale: turnover.male, turnoverFemale: turnover.female, turnoverOther: turnover.other,
					diPolicy:                   res.DI_Policy,
					policyRef:                  res.Policy_Ref                 || "",
					responsibleRole:            res.Responsible_Role           || "",
					hasTargets:                 res.Has_Targets,
					targetsDescription:         res.Targets_Description        || "",
					initiatives:                initiatives.selected           || [],
					initiativesHasOther:        !!initiatives.other,
					initiativesOther:           initiatives.other              || "",
					hasTraining:                res.Has_Training,
					trainingFrequency:          res.Training_Frequency,
					trainingDescription:        res.Training_Description       || "",
					diversityChange:            res.Diversity_Change           || "",
					improvements:               res.Improvements               || "",
					trackMetrics:               metrics.tracked,
					metricsTracked:             metrics.items                  || [],
				}

				if (res) {
					this.mapDataToForm(res)
				}
			},
		},
	},

	computed: {
		genderTotal() {
			return (Number(this.resultData?.genderMale) || 0)
				+ (Number(this.resultData?.genderFemale) || 0)
				+ (Number(this.resultData?.genderOther) || 0)
		},

		allInitiatives() {
			if (!this.resultData) return []

			const list = [...(this.resultData.initiatives || [])]

			if (this.resultData.initiativesOther) {
				list.push(this.resultData.initiativesOther)
			}

			return list
		},

		ageMax() {
			return Math.max(
				...this.ageFields.map(a => Number(this.resultData?.[a.model]) || 0)
			)
		},

		genderMalePct() {
			return this.genderTotal
				? Math.round((this.resultData.genderMale / this.genderTotal) * 100)
				: 0
		},

		genderFemalePct() {
			return this.genderTotal
				? Math.round((this.resultData.genderFemale / this.genderTotal) * 100)
				: 0
		},
	},

	async created() {
		this.loadData()
	},

	methods: {
		toggleDesc(key) {
			this.$set(this.showDesc, key, !this.showDesc[key])
		},
		enableEdit() {
			this.submitted = false
		},

		async loadData() {
			try {
				const res = await CsrAPI.getVsmeDiversity()

				if (!res || !res.Total_Employees) {
					this.submitted = false
					return
				}

				this.hasExistingRecord = true
				this.mapDataToForm(res)
				this.resultData = { ...this.form }

				if (res.Is_Draft === true || res.Is_Draft === 1) {
					// Draft exists — reopen form pre-filled, don't show result view
					this.isDraft = true
					this.submitted = false
				} else if (!this.isEditMode) {
					this.submitted = true
				}

			} catch (error) {
				console.error("Failed to load diversity data:", error)
			}
		},

		mapDataToForm(res) {
			const safeParse = (val) => {
				try {
					return JSON.parse(val || "{}")
				} catch {
					return {}
				}
			}

			const gender      = safeParse(res.Gender_Distribution)
			const leader      = safeParse(res.Leadership_Gender)
			const staff       = safeParse(res.Staff_Gender)
			const age         = safeParse(res.Age_Distribution)
			const promo       = safeParse(res.Promotion_Rate)
			const turnover    = safeParse(res.Turnover_Rate)
			const initiatives = safeParse(res.Initiatives)
			const metrics     = safeParse(res.Metrics_Data)

			this.form = {
				...this.form,

				totalEmployees: res.Total_Employees,

				genderMale: gender.male,
				genderFemale: gender.female,
				genderOther: gender.other,

				leaderMale: leader.male,
				leaderFemale: leader.female,
				leaderOther: leader.other,

				staffMale: staff.male,
				staffFemale: staff.female,
				staffOther: staff.other,

				under25: age.under25,
				age25_34: age.age25_34,
				age35_44: age.age35_44,
				age45_54: age.age45_54,
				age55plus: age.age55plus,

				nationalities: res.Nationalities || "",
				additionalDemographics: res.Additional_Demographics || "",

				trackPayGap: res.Track_Pay_Gap,
				payGap: res.Pay_Gap,

				promoMale: promo.male,
				promoFemale: promo.female,
				promoOther: promo.other,

				turnoverMale: turnover.male,
				turnoverFemale: turnover.female,
				turnoverOther: turnover.other,

				underrepresentedLeadership: res.Underrepresented_Leadership,

				diPolicy: res.DI_Policy,
				policyRef: res.Policy_Ref || "",
				responsibleRole: res.Responsible_Role || "",

				hasTargets: res.Has_Targets,
				targetsDescription: res.Targets_Description || "",

				initiatives: initiatives.selected || [],
				initiativesHasOther: !!initiatives.other,
				initiativesOther: initiatives.other || "",

				hasTraining: res.Has_Training,
				trainingFrequency: res.Training_Frequency,
				trainingDescription: res.Training_Description || "",

				diversityChange: res.Diversity_Change || "",
				improvements: res.Improvements || "",

				trackMetrics: metrics.tracked,
				metricsTracked: metrics.items || [],
			}
		},

		async _saveOrUpdate(isDraft) {
			const f = this.form
			const args = [
				f.totalEmployees, f.genderMale, f.genderFemale, f.genderOther,
				f.leaderMale, f.leaderFemale, f.leaderOther,
				f.staffMale, f.staffFemale, f.staffOther,
				f.under25, f.age25_34, f.age35_44, f.age45_54, f.age55plus,
				f.nationalities, f.additionalDemographics,
				f.trackPayGap, f.payGap,
				f.promoMale, f.promoFemale, f.promoOther,
				f.turnoverMale, f.turnoverFemale, f.turnoverOther,
				f.underrepresentedLeadership,
				f.diPolicy, f.policyRef, f.responsibleRole,
				f.hasTargets, f.targetsDescription,
				f.initiatives, f.initiativesOther,
				f.hasTraining, f.trainingFrequency, f.trainingDescription,
				f.diversityChange, f.improvements,
				f.trackMetrics, f.metricsTracked,
				isDraft,
			]
			if (this.hasExistingRecord) {
				await CsrAPI.updateVsmeDiversity(...args)
			} else {
				await CsrAPI.saveVsmeDiversity(...args)
				this.hasExistingRecord = true   // flip after first insert
			}
		},

		async submit() {
			this.loading = true
			this.errorMessage = ""
			try {
				await this._saveOrUpdate(false)
				this.resultData = { ...this.form }
				this.isDraft = false
				this.submitted = true
				this.$emit("saved")
			} catch (error) {
				this.errorMessage = error.message
			} finally {
				this.loading = false
			}
		},

		async saveDraft() {
			this.loading = true
			this.errorMessage = ""
			try {
				await this._saveOrUpdate(true)
				this.isDraft = true
				this.$emit("drafted")
			} catch (error) {
				this.errorMessage = error.message
			} finally {
				this.loading = false
			}
		},

		toggleDropdown() {
			this.showDownloadDropdown = !this.showDownloadDropdown
		},

		closeDropdown() {
			this.showDownloadDropdown = false
		},

		handleDownloadPDF() {
			this.showDownloadDropdown = false
			CsrAPI.downloadVsmePDF()
		},
		
		handleDownloadJSON() {
			this.showDownloadDropdown = false
			CsrAPI.downloadJSON()
		},

		fmt(val, suffix = "") {
			if (val === null || val === undefined || val === "") {
				return "—"
			}

			return val + suffix
		},

		badge(val) {
			if (val === "Yes") {
				return "<span class='yes-badge'>Yes</span>"
			}

			if (val === "No") {
				return "<span class='no-badge'>No</span>"
			}

			return val || "—"
		},

		agePct(val) {
			return this.ageMax
				? Math.round((Number(val) || 0) / this.ageMax * 100)
				: 0
		},
	},
}
</script>

<style scoped>
.esgcontainer h1 {
  color: #1e7c83;
  font-weight: bold;
}

h2 {
  color: #1e7c83;
}

h3 {
  color: #424242;
  margin-bottom: 8px;
}

.contentRow {
  margin-bottom: 16px;
}

.stepper-container {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 20px;
}

.step-item {
	text-align: center;
	cursor: pointer;
	flex: 1;
	position: relative;
}

.step-circle {
	width: 35px;
	height: 35px;
	border-radius: 50%;
	background: #ddd;
	color: #555;
	margin: auto;
	display: flex;
	align-items: center;
	justify-content: center;
	font-weight: bold;
}

.step-item.active .step-circle {
	background: #1e7c83;
	color: white;
}

.step-item.active .step-label {
	color: #1e7c83;
	font-weight: bold;
}

.icon-circle {
	border: 1px solid currentColor;
	border-radius: 50%;
	padding: 4px;
}

.desc-wrapper {
	padding-top: 0 !important;
	margin-top: -20px;
}

.view-root {
	padding: 1.5rem 0;
	font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
	color: #212121;
}

.stepper-row {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 80px;
}

.step-item { 
	text-align: center; flex: 1; 
}

.step-item.active .step-circle {
	background: #1e7c83;
}

.step-item.active .step-label {
	color: #1e7c83;
}

.step-circle {
	width: 30px; 
	height: 30px; 
	border-radius: 50%;
	background: #ddd; 
	margin: auto;
	display: flex; 
	align-items: center; 
	justify-content: center;
}

.step-label { 
	margin-top: 6px; color: #999; 
}

.header-bar {
	display: flex; 
	align-items: center; 
	justify-content: space-between;
	margin-bottom: 1.25rem;
}

.header-sub   { 
	color: #757575; 
	margin-top: 2px; 
}

.submitted-badge {
	display: flex; 
	align-items: center; 
	gap: 5px;
	background: #e8f5e9; 
	color: #2e7d32;
	padding: 4px 12px; 
	border-radius: 20px;
}

.btn-outline {
	border: 1px solid #bdbdbd; 
	background: none; 
	border-radius: 6px;
	padding: 5px 12px; 
	color: #424242; 
	cursor: pointer;
	display: flex; 
	align-items: center; 
	gap: 4px;
}

.btn-outline:hover { 
	background: #f5f5f5; 
}

.download-wrapper {
	display: flex;
	justify-content: flex-end;
	margin-bottom: 10px; 
}

.download-btn {
	border: 1px solid #bdbdbd;
	background: none;
	border-radius: 6px;
	padding: 5px 12px;
	color: #424242;
	cursor: pointer;
	display: flex;
	align-items: center;
	gap: 4px;
}

.download-dropdown-wrapper {
    position: relative;
    display: inline-block;
}

.download-dropdown-menu {
    position: absolute;
    right: 0;
    top: calc(100% + 4px);
    background: #fff;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    box-shadow: 0 4px 16px rgba(0,0,0,0.10);
    min-width: 160px;
    z-index: 100;
    overflow: hidden;
}

.dropdown-item {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 10px 16px;
    background: none;
    border: none;
    color: #424242;
    cursor: pointer;
    text-align: left;
    font-size: 0.875rem;
    transition: background 0.15s;
}

.dropdown-item:hover {
    background: #f5f5f5;
}

.icon-pdf {
    color: #e53935 !important;  
}

.icon-json {
    color: #f9a825 !important;
}

.section {
	background: #fff;
	border: 1px solid #2d9b9b;
	border-radius: 10px;
	padding: 1.25rem 1.5rem;
	margin-bottom: 1rem;
}
.section-title {
	color: #9e9e9e;
	text-transform: uppercase; 
	letter-spacing: 0.06em; 
	margin: 0 0 1rem;
}

.metric-grid {
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(90px, 1fr));
	gap: 10px; margin-bottom: 0.5rem;
}

.metric-card {
	background: #f5f5f5; 
	border-radius: 8px; 
	padding: 0.75rem 1rem;
}

.metric-label { 
	color: #757575; 
	margin: 0 0 4px; 
}

.metric-value { 
	color: #212121; 
	margin: 0; 
}

.metric-sub { 
	color: #bdbdbd; 
	margin: 2px 0 0; 
}

.bar-wrap { 
	margin-bottom: 10px; 
}

.bar-label {
	display: flex; 
	justify-content: space-between;
	color: #757575; 
	margin-bottom: 4px;
}

.bar-track {
	height: 8px; 
	background: #f0f0f0; 
	border-radius: 4px; 
	overflow: hidden;
}

.bar-fill { 
	height: 100%; 
	border-radius: 4px; 
	transition: width 0.5s ease; 
}

.row-grid {
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
	gap: 12px;
}

.row-item { 
	display: flex; 
	flex-direction: column; 
	gap: 3px; 
}

.row-key { 
	color: #9e9e9e; 
}

.row-val { 
	color: #212121; 
}

.tag-list { 
	display: flex; 
	flex-wrap: wrap; 
	gap: 6px; 
}

.tag {
	background: #e0f2f1; 
	color: #00695c;
	padding: 3px 10px; 
	border-radius: 20px;
}

.empty-note { 
	color: #bdbdbd; 
}

.yes-badge {
	background: #e8f5e9; 
	color: #2e7d32;
	padding: 2px 9px; 
	border-radius: 20px; 
	display: inline-block;
}
.no-badge {
	background: #f5f5f5; 
	color: #757575;
	padding: 2px 9px; 
	border-radius: 20px; 
	display: inline-block;
}

.text-block { 
	background: #f9f9f9; 
	border-radius: 6px; 
	padding: 10px 14px; 
}

.text-body { 
	color: #424242; 
	line-height: 1.6; 
}

.sub-label { 
	color: #9e9e9e; 
	margin-bottom: 8px; 
}

.divider { 
	border: none; 
	border-top: 0.5px solid #e0e0e0; 
	margin: 1rem 0; 
}

.state-center { 
	text-align: center; 
	padding: 3rem 0; 
}

.state-msg { 
	color: #9e9e9e; 
	margin-top: 1rem; 
}

.draft-badge {
    display: flex;
    align-items: center;
    gap: 5px;
    background: #fff8e1;
    color: #f57f17;
    padding: 4px 12px;
    border-radius: 20px;
}

@media print {
  .btn-outline, .stepper-row { display: none !important; }
}
</style>